import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ArkUserType, ClientModel, ClientPermissionViewModel, ClientRoleViewModel, ClientUserViewModel, ClientViewModel } from '../../core/api/models';
import { ClientsApiService, ClientUsersApiService, PermissionsApiService } from '../../core/api/services';
import { Utils } from '../../core/utils';
import { AuthService } from '../../main/auth.service';
import { ClientService } from '../';
import { AlertService } from '../../shared/services/alert.service';

@Component({
  selector: 'app-client-permissions',
  templateUrl: './client-permissions.component.html',
  standalone: false
})
export class ClientPermissionsComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private permissionRequest = 0;
  private userSearchRequest = 0;
  private roleSearchRequest = 0;
  currentItem?: ClientModel;
  clientID = 0;
  loading = true;
  saving = false;
  assignmentsLoaded = false;
  permissions: ClientPermissionViewModel[] = [];
  currentPermission?: ClientPermissionViewModel;
  selectedUsers: ClientUserViewModel[] = [];
  selectedRoles: ClientRoleViewModel[] = [];
  filteredUsers: ClientUserViewModel[] = [];
  filteredRoles: ClientRoleViewModel[] = [];

  constructor(
    private readonly authService: AuthService,
    private readonly clientService: ClientService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly alertService: AlertService,
    private readonly apiService: ClientsApiService,
    private readonly clientUsersApi: ClientUsersApiService,
    private readonly permissionsApi: PermissionsApiService,
    private readonly sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.authService.checkIfAutorized([ArkUserType.Admin]);


     this.clientService.getCurrentClient().subscribe((client) => {

      if (client){
         this.currentItem = client;
          this.clientID = client.ID||0;;
        

      ++this.permissionRequest;
      this.currentPermission = undefined;
      this.permissions = [];
      this.assignmentsLoaded = false;
      this.loading = true;
      if (this.clientID <= 0) {
        this.loading = false;
        return;
      }
      forkJoin({
        permissions: this.clientUsersApi.GetClientPermissions({ clientID: this.clientID||0 })
      }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: data => {
          this.permissions = data.permissions;
          this.loading = false;
        },
        error: error => {
          this.loading = false;
          this.alertService.error(Utils.getAjaxError(error));
        }
      });
    }
    });
  }

  selectPermission(permissionID: number): void {
    if (this.saving) return;
    const permission = this.permissions.find(item => item.PermissionID === permissionID);
    const clientID = this.clientID;
    const request = ++this.permissionRequest;
    this.currentPermission = permission ? { ...permission } : undefined;
    this.assignmentsLoaded = false;
    this.selectedUsers = [];
    this.selectedRoles = [];
    this.filteredUsers = [];
    this.filteredRoles = [];
    if (!permission?.PermissionID || !clientID) return;
    this.loading = true;
    this.permissionsApi.GetClientAssignedPermission({ body: { clientID, permissionID } })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: data => {
          if (this.clientID !== clientID || request !== this.permissionRequest) return;
          // Keep assigned IDs even when a legacy assignment has no matching user or role.
          this.selectedUsers = (permission.UserIDs ?? []).map(id =>
            data.Users?.find(user => user.ID === id) ?? { ID: id, UserName: `User #${id}` });
          this.selectedRoles = (permission.RoleIDs ?? []).map(id =>
            data.Roles?.find(role => role.RoleID === id) ?? { RoleID: id, Role: `Role #${id}` });
          this.assignmentsLoaded = true;
          this.loading = false;
        },
        error: error => {
          if (this.clientID !== clientID || request !== this.permissionRequest) return;
          this.loading = false;
          this.alertService.error(Utils.getAjaxError(error));
        }
      });
  }

  searchUsers(event: { query: string }): void {
    const clientID = this.clientID;
    const permissionRequest = this.permissionRequest;
    const request = ++this.userSearchRequest;
    if (!clientID || !this.assignmentsLoaded) return;
    this.clientUsersApi.Search({ clientID, body: { prefixText: event.query, count: 25 } })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: users => {
          if (this.clientID !== clientID || permissionRequest !== this.permissionRequest || request !== this.userSearchRequest) return;
          this.filteredUsers = users.filter(user => !this.selectedUsers.some(selected => selected.ID === user.ID));
        },
        error: error => {
          if (this.clientID !== clientID || permissionRequest !== this.permissionRequest || request !== this.userSearchRequest) return;
          this.filteredUsers = [];
          this.alertService.error(Utils.getAjaxError(error));
        }
      });
  }

  searchRoles(event: { query: string }): void {
    const clientID = this.clientID;
    const permissionRequest = this.permissionRequest;
    const request = ++this.roleSearchRequest;
    if (!clientID || !this.assignmentsLoaded) return;
    this.clientUsersApi.SearchRoles({ clientID, body: { prefixText: event.query, count: 25 } })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: roles => {
          if (this.clientID !== clientID || permissionRequest !== this.permissionRequest || request !== this.roleSearchRequest) return;
          this.filteredRoles = roles.filter(role => !this.selectedRoles.some(selected => selected.RoleID === role.RoleID));
        },
        error: error => {
          if (this.clientID !== clientID || permissionRequest !== this.permissionRequest || request !== this.roleSearchRequest) return;
          this.filteredRoles = [];
          this.alertService.error(Utils.getAjaxError(error));
        }
      });
  }

  save(): void {
    const clientID = this.clientID;
    const permission = this.currentPermission;
    if (this.loading || this.saving || !this.assignmentsLoaded || !clientID || !permission?.PermissionID) return;
    const body: ClientPermissionViewModel = {
      ...permission,
      UserIDs: this.selectedUsers.map(user => user.ID).filter((id): id is number => id != null),
      RoleIDs: this.selectedRoles.map(role => role.RoleID).filter((id): id is number => id != null)
    };
    this.saving = true;
    this.clientUsersApi.SaveClientPermission({ clientID, body })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: saved => {
          this.saving = false;
          if (this.clientID !== clientID || this.currentPermission?.PermissionID !== permission.PermissionID) return;
          const index = this.permissions.findIndex(item => item.PermissionID === permission.PermissionID);
          if (index >= 0) this.permissions[index] = saved;
          this.currentPermission = undefined;
          this.assignmentsLoaded = false;
          this.alertService.success('Client permission saved successfully');
        },
        error: error => {
          this.saving = false;
          if (this.clientID === clientID)
            this.alertService.error(Utils.getAjaxError(error));
        }
      });
  }

  getClientLogo(client: ClientViewModel | ClientModel | null | undefined, type: string = 'Print'): any {

    var siteName = client?.SiteName?.toLocaleLowerCase();
    if (siteName == 'optodev')
      siteName = 'beta';

    //return this.sanitizer.bypassSecurityTrustUrl(`https://${siteName}.${environment.baseDomain}/Login/DisplayLogo/${siteName}.png`);
    return this.sanitizer.bypassSecurityTrustUrl(`https://${siteName}.${environment.baseDomain}/Login/${type}Logo/${siteName}.png`);
  }
  clickItem(item: string) {
    this.router.navigate([`/client/${this.currentItem?.ID}/${item}`]);
  }

}
